import { Link, useRouter } from "@tanstack/react-router";
import type { JSX } from "react";

import { cn } from "@repo/cn";

import type { NavItemProps } from "@/hooks/use-nav-items";
import { trackEvent } from "@/lib/tracking";

interface NavItemsProps {
  items: Record<string, NavItemProps>;
  className?: string;
  variant?: "default" | "floating";
}

const navItemStyles = {
  default: {
    icon: "fill-neutral-400",
    activeIcon: "fill-secondary-400",
    text: "text-[10px] leading-tight text-neutral-400 truncate",
    activeText: "text-secondary-400",
    content: "flex flex-col items-center gap-0.5",
  },
  floating: {
    icon: "fill-neutral-400",
    activeIcon: "fill-primary-600",
    text: "text-xs leading-[1.32] text-neutral-400 truncate",
    activeText: "font-medium text-primary-600",
    content: "flex flex-col items-center [&>svg]:my-1",
  },
};

export const NavItems = ({
  items,
  className,
  variant = "default",
}: NavItemsProps): JSX.Element => {
  const styles = navItemStyles[variant];
  const router = useRouter();

  const trackClicked = (item: string): void => {
    trackEvent("Navbar Clicada", { item, origem: router.state.location.pathname });
  };

  return (
    <>
      {Object.entries(items).map(([key, option]) => (
        <Link
          aria-label={option.title}
          key={key}
          to={option.url}
          search={option.search}
          className={className ?? "flex w-full justify-center"}
          preload={option.preload ?? "viewport"}
          activeOptions={{ includeSearch: false }}
          onClick={() => trackClicked(option.title)}
        >
          {({ isActive }: { isActive: boolean }) => {
            const Icon = isActive ? option.SelectedIcon : option.Icon;

            return (
              <div className={styles.content}>
                <Icon
                  className={cn(styles.icon, isActive && styles.activeIcon)}
                  size={24}
                />
                <span className={cn(styles.text, isActive && styles.activeText)}>
                  {option.title}
                </span>
              </div>
            );
          }}
        </Link>
      ))}
    </>
  );
};
